/**
 * What accepting a „Meinten Sie“ suggestion writes into the session's own
 * layer (I-3024): the learn rule of `spell_check.corrections`. It lives next
 * to the dictionary's fold (`dictionaryFold.ts`, the search's own fold) and
 * not in `config/spellCheck.ts`, because that entry stays free of the UI
 * graph the fold comes with. Pure functions.
 */
import { isJsonObject, type JsonObject } from '../config/mergePatch';
import {
  SPELL_CHECK_CORRECTIONS_FIELD,
  SPELL_CORRECTION_LEARN_MAX,
  readSpellCorrection,
  type SpellCheckConfig,
  type SpellCorrection,
} from '../config/spellCheck';
import { dictionaryFold } from './dictionaryFold';
import { dictionaryWord, type DictionaryLanguage } from './dictionaryFile';

/** What a learned correction writes: the wrong word as the own layer keeps it, and its value. */
export interface LearnedSpellCorrection {
  /** The key to write: the own entry's key when one exists (a case variant of the typed word), else the typed word. */
  typed: string;
  correction: SpellCorrection;
}

/**
 * The correction `typed → to` as `learned`, counted 1 for a new target and +1
 * for the same one up to `SPELL_CORRECTION_LEARN_MAX`, for the own layer
 * (`own`). The own entry is found by the dictionary's fold of the typed word
 * (the same word the search and master's validator see), so „torblat“ meets an
 * entry stored as „Torblat“ and is written under that key — never as a second
 * entry. Null when nothing is to be written: no word, the typed word is the
 * target (by fold: „strasse“ → „Straße“), the entry is already counted to the
 * maximum for this target, or an entry typed in or imported (never turned into
 * a learned one).
 */
export function learnedSpellCorrection(
  own: SpellCheckConfig | JsonObject | undefined,
  lang: DictionaryLanguage,
  typed: string,
  to: string,
): LearnedSpellCorrection | null {
  const from = dictionaryWord(typed);
  const target = dictionaryWord(to);
  if (!from || !target) return null;
  const fromFold = dictionaryFold(from);
  if (!fromFold || fromFold === dictionaryFold(target)) return null;
  const maps = isJsonObject(own) ? own[SPELL_CHECK_CORRECTIONS_FIELD] : undefined;
  const byLanguage = isJsonObject(maps) ? maps[lang] : undefined;
  let key = from;
  let existing: SpellCorrection | undefined;
  if (isJsonObject(byLanguage)) {
    const keys = Object.keys(byLanguage).filter(
      (candidate) => readSpellCorrection(byLanguage[candidate]) && dictionaryFold(candidate) === fromFold,
    );
    // The exact key wins when the layer holds several variants.
    key = keys.includes(from) ? from : (keys[0] ?? from);
    existing = readSpellCorrection(byLanguage[key]);
  }
  if (existing && existing.origin !== 'learned') return null;
  if (existing && existing.to === target) {
    if (existing.count >= SPELL_CORRECTION_LEARN_MAX) return null;
    return {
      typed: key,
      correction: {
        to: target,
        count: Math.min(existing.count + 1, SPELL_CORRECTION_LEARN_MAX),
        origin: 'learned',
      },
    };
  }
  return { typed: key, correction: { to: target, count: 1, origin: 'learned' } };
}
